import { useMemo, useState } from 'react'
import { useDraggable } from '@dnd-kit/core'
import { Thumb } from './Thumb'
import { Menu } from './Menu'
import { useCollapsedCategories } from '../lib/collapse'
import type { Category, Item } from '@shared/types'

type Tab = 'recent' | 'all'

interface Props {
  categories: Category[]
  items: Item[]
  /** Item ids, most recently packed first. Empty until the first week is planned. */
  recentIds: string[]
  /**
   * The categories the dragged food may be dropped into, or null when nothing
   * is being dragged. Groups outside it recede -- which is more than one
   * group now that Side and Snack accept each other.
   */
  dropTargets: Set<string> | null
  onExpand: (item: Item) => void
  /** Open the item editor for a new food, in this category if given. */
  onAddFood: (categoryId?: string) => void
  /** Open the item editor on a food already in the pantry. */
  onEditFood: (item: Item) => void
  /** Put a food away without opening the editor. Reversible from the Pantry tab. */
  onArchiveFood: (item: Item) => void
}

const RECENT_MAX = 12

/**
 * The docked pantry, shown alongside the board on wide screens so foods can be
 * dragged straight onto a day.
 *
 * Two ways in: All, one category at a time so the list is never a 78-item
 * wall; and Recent, the dozen foods packed most lately, which is most of
 * what a week needs. Search spans everything. A food can be added from here
 * without leaving the board.
 */
export function PantryDock({ categories, items, recentIds, dropTargets, onExpand, onAddFood, onEditFood, onArchiveFood }: Props) {
  const [query, setQuery] = useState('')
  // All by default: the whole pantry, one category at a time. Recent is the shortcut.
  const [tab, setTab] = useState<Tab>('all')
  const { collapsed, toggle } = useCollapsedCategories()

  const needle = query.trim().toLowerCase()
  const live = useMemo(() => items.filter((i) => !i.archived), [items])
  const byId = useMemo(() => new Map(live.map((i) => [i.id, i])), [live])
  const catById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories])

  const matches = useMemo(
    () =>
      needle
        ? live.filter((i) => i.name.toLowerCase().includes(needle) || i.notes.toLowerCase().includes(needle))
        : [],
    [live, needle],
  )

  const recent = useMemo(
    () => recentIds.map((id) => byId.get(id)).filter((i): i is Item => Boolean(i)).slice(0, RECENT_MAX),
    [recentIds, byId],
  )

  const recede = (categoryId: string) => dropTargets !== null && !dropTargets.has(categoryId)

  // Nothing packed yet: a few mains from the pantry, so the first week has
  // somewhere to start that is not a 78-item wall.
  const starters = useMemo(
    () => (recent.length === 0 && categories[0] ? live.filter((i) => i.categoryId === categories[0]!.id).slice(0, 8) : []),
    [recent.length, live, categories],
  )

  return (
    <div className="dock">
      <div className="dock-head">
        <h2>Pantry</h2>
        <span className="muted">{live.length}</span>
        <div className="spacer" />
        <button type="button" className="btn sm dock-add" onClick={() => onAddFood()}>
          + Add a food
        </button>
      </div>
      <input
        className="pantry-search"
        type="search"
        autoComplete="off"
        placeholder="Find a food…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        aria-label="Find a food"
      />

      {needle ? (
        <div className="dock-section">
          {matches.length === 0 ? (
            <p className="muted small">
              Nothing matches “{query}”.{' '}
              <button type="button" className="linklike" onClick={() => onAddFood()}>
                Add it
              </button>
            </p>
          ) : (
            <div className="pantry-list">
              {matches.map((item) => (
                <DraggableItem key={item.id} item={item} sub={catById.get(item.categoryId)?.name} dim={recede(item.categoryId)} onExpand={onExpand} onEdit={onEditFood} onArchive={onArchiveFood} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="dock-tabs" role="tablist" aria-label="Pantry view">
            <button type="button" role="tab" className="btn sm" aria-selected={tab === 'all'} aria-pressed={tab === 'all'} onClick={() => setTab('all')}>
              All
            </button>
            <button type="button" role="tab" className="btn sm" aria-selected={tab === 'recent'} aria-pressed={tab === 'recent'} onClick={() => setTab('recent')}>
              Recent
            </button>
          </div>

          {tab === 'recent' && (
            <div className="dock-section">
              {recent.length === 0 && starters.length > 0 ? (
                <>
                  <div className="dock-label">A few familiar starting points</div>
                  <div className="pantry-list">
                    {starters.map((item) => (
                      <DraggableItem key={item.id} item={item} dim={recede(item.categoryId)} onExpand={onExpand} onEdit={onEditFood} onArchive={onArchiveFood} />
                    ))}
                  </div>
                  <p className="muted small">Everything else is under All.</p>
                </>
              ) : recent.length === 0 ? (
                <p className="muted small">Nothing here yet. Everything is under All.</p>
              ) : (
                <>
                  <div className="dock-label">Recently packed</div>
                  <div className="pantry-list">
                    {recent.map((item) => (
                      <DraggableItem key={item.id} item={item} dim={recede(item.categoryId)} onExpand={onExpand} onEdit={onEditFood} onArchive={onArchiveFood} />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {tab === 'all' && (
            <div className="dock-section">
              {categories.map((category) => {
                const list = live.filter((i) => i.categoryId === category.id)
                const open = !collapsed.has(category.id)
                return (
                  <div className={`dock-cat ${open ? 'open' : ''}`} key={category.id} style={recede(category.id) ? { opacity: 0.35 } : undefined}>
                    <div className="dock-cat-row">
                      <button type="button" className="dock-cat-toggle" aria-expanded={open} onClick={() => toggle(category.id)}>
                        <span className="cat-chevron" aria-hidden="true">
                          {open ? '▾' : '▸'}
                        </span>
                        <span className="slot-dot" style={{ background: category.color }} aria-hidden="true" />
                        <span className="dock-cat-name">{category.name}</span>
                        <span className="count">{list.length}</span>
                      </button>
                      <button type="button" className="dock-cat-add" aria-label={`Add a ${category.name.toLowerCase()}`} title={`Add a ${category.name.toLowerCase()}`} onClick={() => onAddFood(category.id)}>
                        +
                      </button>
                    </div>
                    {open && (
                      <div className="pantry-list">
                        {list.length === 0 && <p className="muted small">None yet.</p>}
                        {list.map((item) => (
                          <DraggableItem key={item.id} item={item} dim={false} onExpand={onExpand} onEdit={onEditFood} onArchive={onArchiveFood} />
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </>
      )}

      <div className="dock-foot">
        <p className="muted small">Drag to a slot, or tap a slot and pick.</p>
      </div>
    </div>
  )
}

function DraggableItem({
  item,
  sub,
  dim,
  onExpand,
  onEdit,
  onArchive,
}: {
  item: Item
  sub?: string
  dim: boolean
  onExpand: (item: Item) => void
  onEdit: (item: Item) => void
  onArchive: (item: Item) => void
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `pantry:${item.id}`,
    data: { kind: 'pantry', itemId: item.id },
  })

  return (
    <div
      ref={setNodeRef}
      className={`pantry-item ${isDragging ? 'dragging' : ''}`}
      style={dim ? { opacity: 0.35 } : undefined}
      title={item.notes || item.name}
      {...listeners}
      {...attributes}
    >
      <Thumb emoji={item.emoji} imageKey={item.imageKey} name={item.name} onExpand={() => onExpand(item)} />
      <span className="name">
        {item.name}
        {(sub || item.notes) && <span className="note">{sub ?? item.notes}</span>}
      </span>
      {/*
        Noticing a food needs fixing -- a wrong tag, a stale note -- happens
        while looking at it here, not on the Pantry tab. The menu carries its
        own data-no-drag, so a press on it is a press and not a pick-up.
      */}
      <Menu
        className="dock-menu"
        trigger="⋯"
        align="right"
        label={`Options for ${item.name}`}
        items={[
          { label: 'Edit food…', icon: '✎', onSelect: () => onEdit(item) },
          { label: 'Archive', icon: '⤓', hint: 'Pantry tab', onSelect: () => onArchive(item) },
        ]}
      />
    </div>
  )
}
